import React, { useState, useEffect } from 'react';
import { Backdrop, CircularProgress, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';
import Axios from 'axios';
import {
  Button,
  Icon,
  PanelGroup,
  Panel,
  Modal,
  Input,
  IconButton,
  Notification,
} from 'rsuite';

import {
  DocumentTablesTypes,
  DocumentTableDataTypes,
} from '../../libs/specParser';
import SpecDocument from '../../libs/SpecDocument';
import Spec from '../../models/Spec';

interface ParseDocumentBackdropProps {
  isLoading: boolean;
  spec: DocumentTablesTypes | null;
  visibility: boolean;
  onClose(): void;
}

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  closeBtn: {
    margin: 'unset !important',
  },
  loader: {
    color: 'black',
  },
  container: {
    maxHeight: '95vh',
    backgroundColor: '#ffffff',
    borderRadius: 3,
  },
  problemsContainer: {
    position: 'relative',
    maxHeight: '90vh !important',
    maxWidth: '95vw !important',
    width: '90vw !important',
    overflow: 'unset !important',
  },
  problemsBody: {
    overflow: 'auto',
    boxShadow: '0px 0px 2px 0px rgba(153,153,153,1)',
    MozBoxShadow: '0px 0px 2px 0px rgba(153,153,153,1)',
    WebkitBoxShadow: '0px 0px 2px 0px rgba(153,153,153,1)',
  },
  problemsContainerTitle: {
    maxWidth: '80vw !important',
  },
}));

type ParsedSpecDocumentType = DocumentTablesTypes | null;

export const ParseDocumentBackdrop = ({
  isLoading,
  spec,
  visibility,
  onClose,
}: ParseDocumentBackdropProps) => {
  const classes = useStyles();
  const [showCodeModal, setShowCodeModal] = useState(false as boolean);
  const [showSpecsModal, setShowSpecsModal] = useState(false as boolean);
  const [parsedSpecDocument, setParsedSpecDocument] = useState(
    null as ParsedSpecDocumentType
  );
  const [selectedSpec, setSelectedSpec] = useState('' as string);
  const [selectedSpecIndex, setSelectedSpecIndex] = useState(0 as number);
  const [selectedCode, setSelectedCode] = useState('' as string);
  const [specIDs, setSpecIDs] = useState([] as string[]);
  const [isFetching, setIsFetching] = useState(false as boolean);
  const [isUploading, setIsUploading] = useState(false as boolean);

  const hideHandler = (event: any) => {
    onClose();
    setParsedSpecDocument(null);
    setSelectedSpec('');
    setSelectedSpecIndex(0);
  };

  const overrideBackgroundClickEvent = (event: any) => event.stopPropagation();

  const showSpecsModalHandler = (index: number) => {
    if (!!parsedSpecDocument) {
      const { tables } = parsedSpecDocument.data[index];
      const spec = JSON.stringify(SpecDocument.convertTables(tables), null, 2);

      setSelectedSpec(spec);
      setSelectedSpecIndex(index);
      setShowSpecsModal(true);
    }
  };

  const hideSpecsModalHandler = () => setShowSpecsModal(false);

  const selectedSpecChangeHandler = (value: string) => {
    setSelectedSpec(value);
    setParsedSpecDocument((prevValue) => {
      if (prevValue && prevValue.data[selectedSpecIndex]) {
        prevValue.data[selectedSpecIndex].spec = value;
      }

      return { ...prevValue } as ParsedSpecDocumentType;
    });
  };

  const showCodeModalHandler = (index: number) => {
    if (!!parsedSpecDocument) {
      const { code } = parsedSpecDocument.data[index];

      setSelectedCode(code);
      setSelectedSpecIndex(index);
      setShowCodeModal(true);
    }
  };

  const hideCodeModalHandler = () => setShowCodeModal(false);

  const selectedCodeChangeHandler = (value: string) => setSelectedCode(value);

  const saveCodeHandler = () => {
    setParsedSpecDocument((prevValue) => {
      if (prevValue && prevValue.data[selectedSpecIndex]) {
        prevValue.data[selectedSpecIndex].code = selectedCode;
      }

      setSelectedCode('');

      return { ...prevValue } as ParsedSpecDocumentType;
    });
  };

  const problemNameChangeHandler = (event: any, index: number) => {
    const value = event.target.value;

    setParsedSpecDocument((prevValue) => {
      if (prevValue && prevValue.data[index] && value) {
        prevValue.data[index].name = value;
      }

      return { ...prevValue } as ParsedSpecDocumentType;
    });
  };

  const addTableHandler = (dataIndex: number) => {
    setParsedSpecDocument((prevValue) => {
      if (prevValue && prevValue.data[dataIndex]) {
        const emptyTable =
          '<table><tr><td><p><strong>Input</strong></p></td><td><p><strong>Output</strong></p></td></tr><tr><td></td><td></td></tr></table>';
        let tables = prevValue.data[dataIndex].tables;
        tables.push(emptyTable);
        tables = SpecDocument.reformatTable(tables, dataIndex);

        prevValue.data[dataIndex].tables = tables;
      }

      return { ...prevValue } as ParsedSpecDocumentType;
    });
  };

  const removeTableHandler = (dataIndex: number, tableIndex: number) => {
    setParsedSpecDocument((prevValue) => {
      if (
        prevValue &&
        prevValue.data[dataIndex] &&
        prevValue.data[dataIndex].tables[tableIndex]
      ) {
        prevValue.data[dataIndex].tables.splice(tableIndex, 1);
      }

      return { ...prevValue } as ParsedSpecDocumentType;
    });
  };

  const uploadSpec = async () => {
    let spec = JSON.parse(selectedSpec);
    let problemName = parsedSpecDocument?.data[selectedSpecIndex].name || '';

    const newSpecifications = {
      data: [...spec.data],
      id: problemName,
      name: problemName.replace(/-/g, ' '),
      description: problemName,
      entry: spec.entry,
      env: spec.env,
    };

    try {
      setIsUploading(true);
      await Axios.post('/admin/spec', newSpecifications);
      Notification['success']({
        title: 'Success',
        description: <div>{problemName} specification created.</div>,
      });

      setSpecIDs((prevValue) => [...prevValue, problemName]);
    } catch (e) {
      Notification['error']({
        title: 'Error',
        description: <div>Failed to create spec: {e.message}</div>,
      });
    } finally {
      setShowSpecsModal(false);
      setIsUploading(false);
    }
  };

  const fetchSpecList = async () => {
    try {
      setIsFetching(true);
      const result = await Spec.fetchList();

      const specIDs: string[] = result.map(({ id }) => id);
      setSpecIDs(specIDs);
      setParsedSpecDocument(spec);
    } catch (error) {
      Notification['error']({
        title: 'Error',
        description: <div>{error.message}</div>,
      });
      hideHandler(error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (visibility) {
      disablePageScroll();
    } else {
      enablePageScroll();
    }
  }, [visibility]);

  useEffect(() => {
    if (visibility) {
      fetchSpecList();
    }
  }, [spec]);

  return (
    <>
      <Modal overflow={true} show={showCodeModal} onHide={hideCodeModalHandler}>
        <Modal.Header>
          <Modal.Title>Source Code</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'unset ' }}>
          <Input
            componentClass="textarea"
            style={{ width: '100%', height: '400px', maxHeight: '400px' }}
            placeholder="Paste code here"
            value={selectedCode}
            onChange={selectedCodeChangeHandler}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={saveCodeHandler} appearance="primary">
            Ok
          </Button>
          <Button onClick={hideCodeModalHandler} appearance="subtle">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showSpecsModal} onHide={hideSpecsModalHandler}>
        <Modal.Header>
          <Modal.Title>Spec</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'unset ' }}>
          <Input
            componentClass="textarea"
            rows={100}
            style={{ width: '100%', height: '400px', maxHeight: '400px' }}
            placeholder="specs generated"
            value={selectedSpec}
            onChange={selectedSpecChangeHandler}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={uploadSpec}
            appearance="primary"
            loading={isUploading}
          >
            Upload
          </Button>
          <Button
            onClick={hideSpecsModalHandler}
            appearance="subtle"
            loading={isUploading}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      <Backdrop
        className={`${classes.backdrop} d-flex flex-column align-items-center justify-content-center`}
        open={visibility}
        onClick={hideHandler}
      >
        <Grid
          container
          spacing={2}
          className={`${classes.container} w-auto m-0`}
          onClick={overrideBackgroundClickEvent}
        >
          <Grid item className="p-2">
            {isLoading || isFetching ? (
              <div className="p-5">
                <CircularProgress className={classes.loader} size={30} />
              </div>
            ) : (
              !!parsedSpecDocument && (
                <div
                  className={`${classes.problemsContainer} problems-container`}
                >
                  <h5
                    className={`blue-txt mt-2 mb-4 ${classes.problemsContainerTitle}`}
                  >
                    {parsedSpecDocument.title}
                  </h5>
                  <IconButton
                    className={`close-btn ${classes.closeBtn}`}
                    appearance="subtle"
                    onClick={hideHandler}
                    icon={<Icon icon="close" />}
                  />
                  <div className={`problems-body ${classes.problemsBody}`}>
                    <PanelGroup>
                      {parsedSpecDocument.data.map(
                        (val: DocumentTableDataTypes, i) => (
                          <Panel
                            header={
                              <div className="panel-controls-container">
                                <h6>
                                  {`${i + 1}. `}
                                  <input
                                    className="problem-name"
                                    onChange={async (e) =>
                                      await problemNameChangeHandler(e, i)
                                    }
                                    value={`${val.name}`}
                                    placeholder="PI000-E000-Problem-Name"
                                  />
                                </h6>
                                {!specIDs.includes(val.name) ? (
                                  <div className="panel-controls">
                                    <p className="small-txt">
                                      Tables found:{' '}
                                      {val.tables ? val.tables.length : 0}
                                    </p>
                                    <IconButton
                                      className="code-btn"
                                      appearance="subtle"
                                      onClick={() => showCodeModalHandler(i)}
                                      icon={<Icon icon="code" />}
                                    />

                                    <IconButton
                                      className="code-btn"
                                      appearance="subtle"
                                      onClick={() => addTableHandler(i)}
                                      icon={<Icon icon="plus" />}
                                    />

                                    <IconButton
                                      className="code-btn"
                                      appearance="subtle"
                                      onClick={() => showSpecsModalHandler(i)}
                                      style={{ backgroundColor: '#3498ff' }}
                                      icon={<Icon icon="save" inverse />}
                                    />
                                  </div>
                                ) : (
                                  <Icon
                                    icon="check-circle"
                                    style={{ color: 'green', fontSize: '17px' }}
                                  />
                                )}
                              </div>
                            }
                          >
                            <ul className="tables-list">
                              {val.tables &&
                                val.tables.map((tableHtml, j) => {
                                  return (
                                    <li>
                                      <Panel
                                        shaded
                                        style={{ position: 'relative' }}
                                      >
                                        <IconButton
                                          className="close-btn"
                                          appearance="subtle"
                                          onClick={() =>
                                            removeTableHandler(i, j)
                                          }
                                          icon={<Icon icon="close" />}
                                        />
                                        <div
                                          key={`table-container${i}-${j}`}
                                          id={`table-container${i}-${j}`}
                                          dangerouslySetInnerHTML={{
                                            __html: tableHtml,
                                          }}
                                        ></div>
                                      </Panel>
                                    </li>
                                  );
                                })}
                            </ul>
                          </Panel>
                        )
                      )}
                    </PanelGroup>
                  </div>
                </div>
              )
            )}
          </Grid>
        </Grid>
      </Backdrop>
    </>
  );
};
